// =============================================================================
// features/crm/components/LivechatGroupCard.tsx — Card agrupado de mensagens
// consecutivas do live chat na timeline do lead (F49-S03).
//
// Por quê: cada mensagem de uma conversa de WhatsApp virava um card próprio
// na timeline — uma conversa de 20 mensagens produzia 20 cards idênticos.
// Ver hooks/crm/livechatTimeline.ts (buildTimelineDisplayItems) para o
// agrupamento (interações consecutivas do live chat no mesmo dia civil).
//
// Nunca renderiza texto de mensagem — só contagem + intervalo de horário
// (metadados estruturais, LGPD doc 17 §8.5). O link "Ver no atendimento"
// leva ao inbox do live chat, onde o acesso à mensagem real é controlado e
// auditado (não é deep-link exato — ver limitação documentada em
// livechatTimeline.ts).
// =============================================================================

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/ui/Button';
import type { TimelineGroupItem } from '../../../hooks/crm/livechatTimeline';
import { formatOrgTime } from '../../../lib/orgDateTime';

import { SpotlightCard } from './SpotlightCard';

function ChatIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      className="w-4 h-4"
      aria-hidden="true"
    >
      <path d="M18 10a8 8 0 1 1-8-8" />
      <path d="M18 2v6h-6" />
      <path d="M7 13s1 2 3 2 3-2 3-2" />
    </svg>
  );
}

interface LivechatGroupCardProps {
  item: TimelineGroupItem;
}

export function LivechatGroupCard({ item }: LivechatGroupCardProps): React.JSX.Element {
  const navigate = useNavigate();

  const firstTime = formatOrgTime(item.firstAt);
  const lastTime = formatOrgTime(item.lastAt);
  // Ordem cronológica: `firstAt` é a mais antiga do grupo, `lastAt` a mais
  // recente (a timeline está ordenada do mais recente para o mais antigo).
  const rangeLabel = firstTime === lastTime ? firstTime : `${firstTime} – ${lastTime}`;

  return (
    <SpotlightCard className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span
          className="flex items-center gap-1.5 font-sans font-semibold text-xs uppercase"
          style={{ color: 'var(--brand-verde)', letterSpacing: '0.08em' }}
        >
          <ChatIcon />
          WhatsApp
        </span>
        <span className="font-sans text-xs text-ink-4">{rangeLabel}</span>
      </div>

      <p className="font-sans text-sm text-ink-2 leading-relaxed">
        {item.count} mensagens trocadas no WhatsApp
      </p>

      <div className="mt-2.5 -ml-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate('/conversas')}
          className="text-azul"
        >
          Ver no atendimento
        </Button>
      </div>
    </SpotlightCard>
  );
}
